import React, { useEffect, useState, useMemo } from "react";
import { Alert } from "@material-ui/lab";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { KeyboardTimePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { format, addHours, isToday, isPast } from "date-fns";
import TextField from "@material-ui/core/TextField";
import moment from "moment";

import { SDCloudBackendAPIWithToken } from "../../../apis/backendAPI";
import LoaderHoc from "../../../components/HOC/LoaderHoc";
import { Api } from "../../../config/api";

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  labTable: {
    marginBottom: theme.spacing(2),
    "& td": {
      verticalAlign: "middle !important",
    },
  },
  selectedRow: {
    background: "rgba(0, 174, 239, 0.06)",
  },
  submitBtn: {
    background: `${theme.palette.buttonColor?.main || "#00AEEF"} !important`,
    color: "white",
    height: 40,
    fontFamily: `${theme.palette.fontList?.selectedFont || "inherit"} !important`,
  },
  pickerCell: {
    minWidth: 180,
    verticalAlign: "middle !important",
  },
  durationCell: {
    minWidth: 120,
    verticalAlign: "middle !important",
  },
  durationInput: {
    "& .MuiOutlinedInput-input": {
      padding: "10px 12px",
    },
  },
  checkbox: {
    "&:hover, &.Mui-focused, &:active": {
      backgroundColor: "transparent !important",
    },
  },
  schedulesTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
  },
}));

const isSchedulableResource = (resourceType) => {
  const t = resourceType?.toLowerCase() || "";
  return t.includes("poc") || t.includes("sandbox") || t.includes("learning");
};

const ScheduleLabsTab = ({ reference_id }) => {
  const classes = useStyles();

  const isAdmin = (() => {
    try {
      return JSON.parse(localStorage.getItem("profileData"))?.privilege === "admin";
    } catch {
      return false;
    }
  })();

  const [labs, setLabs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLabs, setSelectedLabs] = useState([]);

  // Per-lab settings state
  const [labSettings, setLabSettings] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("error");

  // Scheduled sandboxes (GCP scheduler tasks) scoped to this training's participants
  const [participantUsernames, setParticipantUsernames] = useState([]);
  const [adminSandboxSchedules, setAdminSandboxSchedules] = useState([]);
  const [isLoadingAdminSchedules, setIsLoadingAdminSchedules] = useState(true);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editDate, setEditDate] = useState(null);
  const [editTime, setEditTime] = useState(null);
  const [editDuration, setEditDuration] = useState(2);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelingGroup, setCancelingGroup] = useState(null);

  const [isActionLoading, setIsActionLoading] = useState(false);

  const handleOpenEdit = (group) => {
    setEditingGroup(group);
    const originalDate = moment.utc(group.schedule_start_time).local().toDate();
    setEditDate(originalDate);
    setEditTime(originalDate);

    let duration = group.hours_end_time;
    if (duration === undefined || duration === null) {
      try {
        const input = typeof group.instructor_input === 'string'
          ? JSON.parse(group.instructor_input)
          : group.instructor_input;
        duration = input?.hours_end_time;
      } catch (e) {
        duration = 2;
      }
    }
    setEditDuration(parseInt(duration) || 2);

    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setEditingGroup(null);
  };

  const handleCancelSchedule = (group) => {
    setCancelingGroup(group);
    setCancelDialogOpen(true);
  };

  const handleCloseCancel = () => {
    setCancelDialogOpen(false);
    setCancelingGroup(null);
  };

  const confirmCancelSchedule = () => {
    if (!cancelingGroup) return;

    setIsActionLoading(true);
    const localDate = moment.utc(cancelingGroup.schedule_start_time).local().toDate();
    const clusters = [{
      resource_name: cancelingGroup.template_name || cancelingGroup.cluster_name || cancelingGroup.resource_name,
      resource_type: cancelingGroup.resource_type?.toLowerCase(),
      provision_date: format(localDate, "yyyy-MM-dd"),
      provision_time: format(localDate, "HH:mm"),
      selected: true,
      instructor_input: {
        clustername: cancelingGroup.template_name || cancelingGroup.cluster_name || cancelingGroup.resource_name,
        resource_type: cancelingGroup.resource_type?.toLowerCase(),
        start_time: format(localDate, "yyyy-MM-dd HH:mm"),
        identifier: cancelingGroup.template_name || cancelingGroup.cluster_name || cancelingGroup.resource_name,
      }
    }];

    SDCloudBackendAPIWithToken()
      .post("instructer/unschedule_training_clusters", { reference_id, clusters })
      .then(() => {
        setIsActionLoading(false);
        setCancelDialogOpen(false);
        setCancelingGroup(null);
        setNotificationType("success");
        setNotificationMessage(`Cancelled schedule for ${cancelingGroup.labName}.`);
        setShowNotification(true);
        fetchAdminSandboxSchedules();
      })
      .catch((err) => {
        setIsActionLoading(false);
        setNotificationType("error");
        setNotificationMessage(err?.response?.data?.message || "Failed to cancel schedule.");
        setShowNotification(true);
      });
  };

  const handleUpdateSchedule = () => {
    if (!editDate || !editTime || !editingGroup) return;

    const startDateTime = new Date(
      format(new Date(editDate), "yyyy-MM-dd") + "T" + format(new Date(editTime), "HH:mm:00")
    );

    if (isPast(startDateTime)) {
      setNotificationType("error");
      setNotificationMessage("Please select a future date and time.");
      setShowNotification(true);
      return;
    }

    const resourceName = editingGroup.template_name || editingGroup.cluster_name || editingGroup.resource_name;
    const lab = labs.find(l => l.resource_name === resourceName);
    const isLearning = editingGroup.resource_type?.toLowerCase() === "learning";
    if (lab && !isLearning && editDuration > lab.allocated_hours) {
      setNotificationType("error");
      setNotificationMessage(`Duration exceeds allocated hours (${lab.allocated_hours}).`);
      setShowNotification(true);
      return;
    }

    setIsActionLoading(true);

    const dateStr = format(new Date(editDate), "yyyy-MM-dd");
    const timeStr = format(new Date(editTime), "HH:mm");
    const formattedDateTime = format(startDateTime, "yyyy-MM-dd HH:mm");
    const suspendTimeObj = addHours(startDateTime, editDuration);
    const suspendDateTimeStr = format(suspendTimeObj, "yyyy-MM-dd HH:mm");

    const instructorInput = {
      clustername: resourceName,
      resource_type: editingGroup.resource_type?.toLowerCase(),
      auto_terminate: "True",
      purpose: "Customer Delivery",
      instance_type: "standard",
      archive: "unarchive",
      sandbox_suspend_time: suspendDateTimeStr,
      identifier: resourceName,
      url: `${Api.baseURL}/`,
      start_time: formattedDateTime,
      input_action: "schedule",
      hours_end_time: editDuration,
    };

    const clusters = [{
      resource_name: resourceName,
      resource_type: editingGroup.resource_type?.toLowerCase(),
      provision_date: dateStr,
      provision_time: timeStr,
      selected: true,
      instructor_input: instructorInput,
    }];

    SDCloudBackendAPIWithToken()
      .post("instructer/update_training_clusters", { reference_id, clusters })
      .then(() => {
        setIsActionLoading(false);
        setEditDialogOpen(false);
        setNotificationType("success");
        setNotificationMessage(`Updated schedule for ${editingGroup.labName}.`);
        setShowNotification(true);
        fetchAdminSandboxSchedules(() => {
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        });
      })
      .catch((err) => {
        setIsActionLoading(false);
        setNotificationType("error");
        setNotificationMessage(err?.response?.data?.message || "Failed to update schedule.");
        setShowNotification(true);
      });
  };

  const groupedSchedules = useMemo(() => {
    const groups = {};
    adminSandboxSchedules.forEach((item) => {
      const labName = item.display_name || item.cluster_name || item.template_name;
      const localStart = moment.utc(item.schedule_start_time).local().format("DD MMM YYYY, hh:mm A");
      const key = `${labName}-${localStart}-${item.resource_type}`;
      if (!groups[key]) {
        groups[key] = {
          ...item,
          labName,
          localStart,
          users: item.user ? [item.user] : [],
        };
      } else {
        if (item.user && !groups[key].users.includes(item.user)) {
          groups[key].users.push(item.user);
        }
      }
    });
    return Object.values(groups);
  }, [adminSandboxSchedules]);

  const scheduledLabWindows = useMemo(() => {
    const windows = {};
    adminSandboxSchedules.forEach((item) => {
      const name = item.display_name || item.cluster_name || item.template_name;
      const start = moment.utc(item.schedule_start_time).local();

      // Try to get duration from hours_end_time directly, then from instructor_input, else default to 2
      let duration = item.hours_end_time;
      if (duration === undefined || duration === null) {
        try {
          const input = typeof item.instructor_input === 'string'
            ? JSON.parse(item.instructor_input)
            : item.instructor_input;
          duration = input?.hours_end_time;
        } catch (e) {
          duration = 2;
        }
      }
      duration = parseInt(duration) || 2;

      const end = moment(start).add(duration, "hours");
      if (!windows[name]) windows[name] = [];
      windows[name].push({ start, end });
    });
    return windows;
  }, [adminSandboxSchedules]);

  // Returns every scheduled time window for a lab, matching the same lab name
  // (fuzzy match handles template/cluster/display-name variants).
  const getScheduledWindowsForLab = (labName) => {
    const targetName = labName.toLowerCase();
    const matchingKeys = Object.keys(scheduledLabWindows).filter((key) => {
      const k = key.toLowerCase();
      return k === targetName || k.includes(targetName) || targetName.includes(k);
    });
    return matchingKeys.reduce((acc, k) => acc.concat(scheduledLabWindows[k]), []);
  };

  // Duplicate guard: a lab already has a Pending/active schedule for this training's
  // scope. Enforced at scheduling time so we never create a second Cloud Scheduler
  // job for the same lab + scope (which would otherwise only surface at provisioning).
  const hasPendingSchedule = (labName) => getScheduledWindowsForLab(labName).length > 0;

  // Pull scheduled sandboxes and scope them to this training's participants.
  const fetchAdminSandboxSchedules = (callback) => {
    if (!isAdmin) {
      if (typeof callback === 'function') callback();
      return;
    }
    setIsLoadingAdminSchedules(true);
    SDCloudBackendAPIWithToken()
      .post(Api.getScheduleCluster, { name: "list" })
      .then((resp) => {
        const list = resp?.data?.list || [];
        const usernames = new Set(participantUsernames);
        const sandboxes = list.filter(
          (item) =>
            item.deleted === false &&
            isSchedulableResource(item.resource_type) &&
            item.user &&
            usernames.has(item.user)
        );
        setAdminSandboxSchedules(sandboxes);
        setIsLoadingAdminSchedules(false);
        if (typeof callback === 'function') callback();
      })
      .catch(() => {
        setIsLoadingAdminSchedules(false);
        if (typeof callback === 'function') callback();
      });
  };

  // Re-scope admin sandbox schedules whenever the participant list (per training) changes.
  useEffect(() => {
    if (isAdmin) {
      fetchAdminSandboxSchedules();
    }
  }, [reference_id, participantUsernames, isAdmin]);

  useEffect(() => {
    SDCloudBackendAPIWithToken()
      .post("instructer/instructer_user_details", { reference_id })
      .then((resp) => resp.data.user_training_details)
      .then((details) => {
        const seen = new Set();
        const uniqueLabs = [];
        const initialSettings = {};
        const usernames = new Set();
        details.forEach((item) => {
          if (item.username) usernames.add(item.username);
          if (!seen.has(item.sku_name)) {
            seen.add(item.sku_name);
            uniqueLabs.push({
              resource_name: item.sku_name,
              resource_type: item.resource_type,
              allocated_quota: item.allocated_quota,
              allocated_hours: parseInt(item.allocated_hours) || 0,
            });
            initialSettings[item.sku_name] = {
              provisionDate: null,
              provisionTime: null,
              hoursEndTime: item.resource_type?.toLowerCase() === "learning" ? (parseInt(item.allocated_quota) || 0) : 2
            };
          }
        });
        setLabs(uniqueLabs);
        setLabSettings(initialSettings);
        setParticipantUsernames(Array.from(usernames));
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        setShowNotification(true);
        setNotificationType("error");
        setNotificationMessage("Failed to load labs for this training.");
      });
  }, [reference_id]);

  const handleToggleLab = (labName) => {
    setSelectedLabs((prev) =>
      prev.includes(labName) ? prev.filter((name) => name !== labName) : [...prev, labName]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLabs(labs.map((l) => l.resource_name));
    } else {
      setSelectedLabs([]);
    }
  };

  const updateLabSetting = (labName, key, value) => {
    setLabSettings(prev => ({
      ...prev,
      [labName]: {
        ...prev[labName],
        [key]: value
      }
    }));
  };

  const isSubmitDisabled =
    selectedLabs.length === 0 ||
    selectedLabs.some(labName => {
      const settings = labSettings[labName];
      const lab = labs.find(l => l.resource_name === labName);
      if (!settings.provisionDate || !settings.provisionTime) return true;
      const d = new Date(settings.provisionDate);
      const t = new Date(settings.provisionTime);
      if (isNaN(d) || isNaN(t)) return true;

      const startDateTime = new Date(
        format(d, "yyyy-MM-dd") + "T" + format(t, "HH:mm:00")
      );

      // Validation: Start time should be in the future
      if (isPast(startDateTime)) return true;

      // Validation: Duration should not exceed allocated hours (for Sandboxes)
      const isLearning = lab?.resource_type?.toLowerCase() === "learning";
      if (lab && !isLearning && settings.hoursEndTime > lab.allocated_hours) return true;

      // Duplicate Guard: block if this lab already has a Pending/active schedule,
      // regardless of whether the requested time window overlaps it.
      if (hasPendingSchedule(labName)) return true;

      return false;
    }) ||
    isSubmitting;

  const handleSubmit = () => {
    // Final Pre-submission Duplicate Guard: never invoke the API if any selected lab
    // already has a Pending/active schedule for this scope. This stops duplicate Cloud
    // Scheduler jobs at scheduling time rather than at provisioning (Ready) state.
    const duplicateLabs = selectedLabs.filter((labName) => hasPendingSchedule(labName));

    if (duplicateLabs.length > 0) {
      setNotificationType("error");
      setNotificationMessage(
        `Already scheduled: ${duplicateLabs.join(", ")}. Cancel the existing schedule below before scheduling again.`
      );
      setShowNotification(true);
      return;
    }

    const clusters = labs.map((lab) => {
      const isSelected = selectedLabs.includes(lab.resource_name);
      let instructorInput = {};
      let dateStr = "";
      let timeStr = "";

      if (isSelected) {
        const settings = labSettings[lab.resource_name];
        let startDateTime, formattedDateTime, suspendDateTimeStr;

        try {
          startDateTime = new Date(
            format(new Date(settings.provisionDate), "yyyy-MM-dd") + "T" + format(new Date(settings.provisionTime), "HH:mm:00")
          );
          dateStr = format(new Date(settings.provisionDate), "yyyy-MM-dd");
          timeStr = format(new Date(settings.provisionTime), "HH:mm");
          formattedDateTime = format(startDateTime, "yyyy-MM-dd HH:mm");
          const suspendTimeObj = addHours(startDateTime, settings.hoursEndTime);
          suspendDateTimeStr = format(suspendTimeObj, "yyyy-MM-dd HH:mm");
        } catch {
          // This shouldn't happen due to isSubmitDisabled, but for safety:
          return null;
        }

        instructorInput = {
          clustername: lab.resource_name,
          resource_type: lab.resource_type.toLowerCase(),
          auto_terminate: "True",
          purpose: "Customer Delivery",
          instance_type: "standard",
          archive: "unarchive",
          sandbox_suspend_time: suspendDateTimeStr,
          identifier: lab.resource_name,
          url: `${Api.baseURL}/`,
          start_time: formattedDateTime,
          input_action: "schedule",
          hours_end_time: settings.hoursEndTime,
        };
      }

      return {
        resource_name: lab.resource_name,
        resource_type: lab.resource_type.toLowerCase(),
        provision_date: dateStr,
        provision_time: timeStr,
        selected: isSelected,
        instructor_input: instructorInput,
      };
    });

    // Check if any cluster processing failed
    if (clusters.some(c => c === null)) {
      setShowNotification(true);
      setNotificationType("error");
      setNotificationMessage("Invalid date or time selection for one or more labs.");
      return;
    }

    setIsSubmitting(true);
    setShowNotification(false);
    setSelectedLabs([]);

    SDCloudBackendAPIWithToken()
      .post("instructer/schedule_training_clusters", { reference_id, clusters })
      .then((resp) => resp.data)
      .then((data) => {
        setIsSubmitting(false);
        setShowNotification(true);
        const alreadyScheduled =
          data.details?.some((p) =>
            p.clusters?.some((c) => {
              const m = c.message?.toLowerCase() || "";
              return m === "already scheduled" || m === "you already have a running cluster.";
            })
          ) ?? false;
        setNotificationType(alreadyScheduled ? "warning" : "success");
        setNotificationMessage(
          alreadyScheduled
            ? `Some clusters were already scheduled. ${data.clusters_scheduled}/${data.clusters_total} active across ${data.participants_processed} participant(s).`
            : `Scheduled: ${data.clusters_scheduled}/${data.clusters_total} clusters across ${data.participants_processed} participant(s). See Active Schedules below.`
        );
        fetchAdminSandboxSchedules();
      })
      .catch((err) => {
        setIsSubmitting(false);
        const errorData = err?.response?.data;
        if (errorData && errorData.details) {
          const isAlreadyScheduledMsg = (msg) => {
            const m = msg?.toLowerCase() || "";
            return m === "already scheduled" || m === "you already have a running cluster.";
          };
          const allAlreadyScheduled = errorData.details.every((p) =>
            p.clusters?.every((c) => isAlreadyScheduledMsg(c.message))
          );
          const anyAlreadyScheduled = errorData.details.some((p) =>
            p.clusters?.some((c) => isAlreadyScheduledMsg(c.message))
          );
          setShowNotification(true);
          setNotificationType("warning");
          setNotificationMessage(
            allAlreadyScheduled
              ? "All clusters are already scheduled. See Active Schedules below."
              : anyAlreadyScheduled
              ? "Some clusters were already scheduled; others could not be scheduled. See Active Schedules below."
              : `Could not schedule all clusters${
                  errorData.clusters_scheduled != null && errorData.clusters_total != null
                    ? ` — ${errorData.clusters_scheduled}/${errorData.clusters_total} succeeded`
                    : ""
                }. See Active Schedules below.`
          );
          fetchAdminSandboxSchedules();
        } else {
          setShowNotification(true);
          setNotificationType("error");
          setNotificationMessage(
            errorData?.message || "Failed to schedule training clusters."
          );
        }
      });
  };


  return (
    <Grid container spacing={2}>
      {showNotification && (
        <Grid item xs={12}>
          <Alert severity={notificationType} onClose={() => setShowNotification(false)}>
            {notificationMessage}
          </Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Select labs and provision date/time for all participants
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <LoaderHoc isLoading={isLoading}>
          {labs.length === 0 && !isLoading ? (
            <Typography variant="body2" color="textSecondary">
              No labs found for this training.
            </Typography>
          ) : (
            <Table size="small" className={classes.labTable}>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 48 }}>
                    <Checkbox
                      className={classes.checkbox}
                      disableRipple
                      indeterminate={
                        selectedLabs.length > 0 && selectedLabs.length < labs.length
                      }
                      checked={labs.length > 0 && selectedLabs.length === labs.length}
                      onChange={handleSelectAll}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>Lab Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Allocated Quota</TableCell>
                  <TableCell className={classes.pickerCell}>Provision Date</TableCell>
                  <TableCell className={classes.pickerCell}>Provision Time</TableCell>
                  <TableCell className={classes.durationCell}>Duration (Hours)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {labs.map((lab) => {
                  const isSelected = selectedLabs.includes(lab.resource_name);
                  const settings = labSettings[lab.resource_name] || {};
                  const isLearningType = lab.resource_type?.toLowerCase() === "learning";

                  return (
                    <TableRow
                      key={lab.resource_name}
                      className={isSelected ? classes.selectedRow : ""}
                    >
                      <TableCell>
                        <Checkbox
                          className={classes.checkbox}
                          disableRipple
                          checked={isSelected}
                          onChange={() => handleToggleLab(lab.resource_name)}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {lab.resource_name}
                        {(() => {
                          const windows = getScheduledWindowsForLab(lab.resource_name);
                          if (windows.length === 0) return null;

                          // Informational only. A lab with an existing schedule is
                          // blocked from rescheduling via the disabled submit button —
                          // no in-table error, so the duplicate guard has one signal.
                          return (
                            <Typography variant="caption" color="textSecondary" display="block">
                              (Active slot{windows.length > 1 ? "s" : ""}: {windows.length})
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell>{lab.resource_type}</TableCell>
                      <TableCell>{lab.allocated_quota}</TableCell>
                      <TableCell className={classes.pickerCell}>
                        {isSelected && (
                          <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <KeyboardDatePicker
                              inputVariant="filled"
                              format="yyyy-MM-dd"
                              disablePast
                              value={settings.provisionDate}
                              onChange={(date) => updateLabSetting(lab.resource_name, 'provisionDate', date)}
                              label=""
                              fullWidth
                              size="small"
                            />
                          </MuiPickersUtilsProvider>
                        )}
                      </TableCell>
                      <TableCell className={classes.pickerCell}>
                        {isSelected && (
                          <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <KeyboardTimePicker
                              inputVariant="filled"
                              value={settings.provisionTime}
                              onChange={(time) => updateLabSetting(lab.resource_name, 'provisionTime', time)}
                              minTime={isToday(settings.provisionDate) ? new Date() : undefined}
                              label=""
                              fullWidth
                              size="small"
                            />
                          </MuiPickersUtilsProvider>
                        )}
                      </TableCell>
                      <TableCell className={classes.durationCell}>
                        {isSelected && (
                          <>
                            <TextField
                              type="number"
                              variant="outlined"
                              size="small"
                              fullWidth
                              disabled={isLearningType}
                              className={classes.durationInput}
                              value={settings.hoursEndTime}
                              onChange={(e) => updateLabSetting(lab.resource_name, 'hoursEndTime', parseInt(e.target.value) || 0)}
                              error={!isLearningType && settings.hoursEndTime > lab.allocated_hours}
                              InputProps={{ inputProps: { min: 1, max: 720 } }}
                            />
                            {!isLearningType && settings.hoursEndTime > lab.allocated_hours && (
                              <Typography variant="caption" color="error">
                                Max: {lab.allocated_hours} hrs
                              </Typography>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </LoaderHoc>
      </Grid>

      <Grid item xs={12}>
        <Button
          variant="contained"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          className={isSubmitDisabled ? "" : classes.submitBtn}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isSubmitting ? "Scheduling…" : "Schedule for All Participants"}
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" className={classes.schedulesTitle}>
          Active Schedules for this Training
        </Typography>
        <LoaderHoc isLoading={isLoadingAdminSchedules}>
          {groupedSchedules.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No active schedules for this training yet. Select labs above and set a provision date/time to schedule for all participants.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Lab Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Participants</TableCell>
                  <TableCell>Start Time (Local)</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedSchedules.map((group) => {
                  return (
                    <TableRow key={`${group.labName}-${group.localStart}-${group.resource_type}`}>
                      <TableCell>{group.labName}</TableCell>
                      <TableCell>{group.resource_type}</TableCell>
                      <TableCell>
                        {group.users.length === participantUsernames.length && participantUsernames.length > 0
                          ? "All Participants"
                          : `${group.users.length} Participant(s)`}
                      </TableCell>
                      <TableCell>{group.localStart}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenEdit(group)} disabled={isActionLoading}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleCancelSchedule(group)} disabled={isActionLoading}>
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </LoaderHoc>
      </Grid>

      <Dialog open={editDialogOpen} onClose={handleCloseEdit} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Schedule: {editingGroup?.labName}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  inputVariant="outlined"
                  format="yyyy-MM-dd"
                  disablePast
                  value={editDate}
                  onChange={setEditDate}
                  label="Provision Date"
                  fullWidth
                  size="small"
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={12}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardTimePicker
                  inputVariant="outlined"
                  value={editTime}
                  onChange={setEditTime}
                  minTime={isToday(editDate) ? new Date() : undefined}
                  label="Provision Time"
                  fullWidth
                  size="small"
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                fullWidth
                label="Duration (Hours)"
                value={editDuration}
                onChange={(e) => setEditDuration(parseInt(e.target.value) || 0)}
                InputProps={{ inputProps: { min: 1, max: 720 } }}
                disabled={editingGroup?.resource_type?.toLowerCase() === "learning"}
                error={(() => {
                  const resourceName = editingGroup?.template_name || editingGroup?.cluster_name || editingGroup?.resource_name;
                  const lab = labs.find(l => l.resource_name === resourceName);
                  const isLearning = editingGroup?.resource_type?.toLowerCase() === "learning";
                  return lab && !isLearning && editDuration > lab.allocated_hours;
                })()}
                helperText={(() => {
                  const resourceName = editingGroup?.template_name || editingGroup?.cluster_name || editingGroup?.resource_name;
                  const lab = labs.find(l => l.resource_name === resourceName);
                  const isLearning = editingGroup?.resource_type?.toLowerCase() === "learning";
                  if (lab && !isLearning && editDuration > lab.allocated_hours) {
                    return `Max: ${lab.allocated_hours} hrs`;
                  }
                  return "";
                })()}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseEdit} color="secondary" disabled={isActionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateSchedule}
            color="primary"
            variant="contained"
            disabled={
              isActionLoading ||
              !editDate ||
              !editTime ||
              (() => {
                const resourceName = editingGroup?.template_name || editingGroup?.cluster_name || editingGroup?.resource_name;
                const lab = labs.find((l) => l.resource_name === resourceName);
                const isLearning = editingGroup?.resource_type?.toLowerCase() === "learning";
                return lab && !isLearning && editDuration > lab.allocated_hours;
              })()
            }
            startIcon={isActionLoading ? <CircularProgress size={16} color="inherit" /> : null}
            className={classes.submitBtn}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={handleCloseCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Schedule</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to cancel the schedule for <strong>{cancelingGroup?.labName}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
            This will unschedule the lab for all participants in this time slot.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseCancel} color="secondary" disabled={isActionLoading}>
            Back
          </Button>
          <Button
            onClick={confirmCancelSchedule}
            color="secondary"
            variant="contained"
            style={{ backgroundColor: '#f44336', color: 'white' }}
            disabled={isActionLoading}
            startIcon={isActionLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Confirm Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ScheduleLabsTab;
